import { useEffect } from "react";
import { MultiSelect } from "@volca/ui/components/ui/multi-select";
import { useGetReviewQueueVendors } from "@/accounts-payable/query";

/** Offers only the vendors with invoices in the queue, plus any chosen. */
export const VendorFilter = ({
  businessId,
  vendorIds,
  onVendorIdsChange,
}: {
  businessId: string | null;
  vendorIds: string[];
  onVendorIdsChange: (ids: string[]) => void;
}) => {
  const {
    data: vendors = [],
    isPending,
    isError,
    isSuccess,
    isPlaceholderData,
  } = useGetReviewQueueVendors(businessId, vendorIds);

  // The list always includes the chosen vendors this business has, so a chosen
  // id it lacks is another business's (carried over a business switch) or a
  // deleted vendor's. Drop it, or it would filter the queue unseen: the chip
  // can only name, and clear, the options it has.
  const shown =
    isSuccess && !isPlaceholderData
      ? vendorIds.filter((id) => vendors.some((vendor) => vendor.id === id))
      : vendorIds;
  const needsRewrite = shown.length !== vendorIds.length;
  useEffect(() => {
    if (needsRewrite) onVendorIdsChange(shown);
  }, [needsRewrite, shown, onVendorIdsChange]);

  return (
    <MultiSelect
      variant="filter"
      label="Vendor"
      placeholder="Any"
      loading={isPending}
      options={[
        ...vendors,
        // Named until the list can say who they are, so the chip never reads
        // "Any" (with nothing to clear) while the queue is filtered by them.
        ...vendorIds
          .filter((id) => !vendors.some((vendor) => vendor.id === id))
          .map((id) => ({
            id,
            name: isError ? "Unknown vendor" : "Loading…",
          })),
        ...(isError
          ? [{ id: "error", name: "Couldn't load vendors", disabled: true }]
          : []),
      ]}
      value={vendorIds}
      onChange={onVendorIdsChange}
    />
  );
};
